import React, { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Checkbox, Col, Form,
  Input, message, Modal, notification, Popconfirm, Row, Select, Space, Spin,
  Table, Tabs, Tag, Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import api from '../api/client';
import useAuthStore from '../store/authStore';
import IndividualFormModal from '../components/IndividualFormModal';
import IndividualInfoDrawer from '../components/IndividualInfoDrawer';
import CounterpartyInfoDrawer from '../components/CounterpartyInfoDrawer';

const { Title } = Typography;

// ── Types ──────────────────────────────────────────────────────────────────────

interface CounterpartyRole { role: string; label: string; contract_note: string | null; }
interface Counterparty {
  id: number;
  short_name: string;
  full_name?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  org_type?: string;
  legal_address?: string;
  actual_address?: string;
  phone?: string;
  email?: string;
  website?: string;
  notes?: string;
  is_active: boolean;
  roles: CounterpartyRole[];
}

// ── Constants ──────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  supplier: 'Поставщик',
  buyer: 'Покупатель',
  carrier: 'Перевозчик',
  bank: 'Банк',
  government: 'Государственный орган',
};

const ROLE_COLORS: Record<string, string> = {
  supplier: 'blue',
  buyer: 'green',
  carrier: 'orange',
  bank: 'purple',
  government: 'red',
};

const ROLE_OPTIONS = Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }));

// ── Main page ──────────────────────────────────────────────────────────────────

const CounterpartiesPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('counterparties:write');

  // Table state
  const [items, setItems] = useState<Counterparty[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string | undefined>();
  const [includeInactive, setIncludeInactive] = useState(false);

  // Drawer (view)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  const [selected, setSelected] = useState<Counterparty | null>(null);
  const [indModalOpen, setIndModalOpen] = useState(false);
  const [contacts, setContacts] = useState<any[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [editIndividualId, setEditIndividualId] = useState<number | null>(null);
  const [editIndModalOpen, setEditIndModalOpen] = useState(false);

  // Individual info drawer
  const [indInfoOpen, setIndInfoOpen] = useState(false);
  const [selectedIndId, setSelectedIndId] = useState<number | null>(null);

  const openIndInfoDrawer = (id: number) => {
    setSelectedIndId(id);
    setIndInfoOpen(true);
  };

  // Secondary counterparty drawer (opened from individual info)
  const [secondaryCpOpen, setSecondaryCpOpen] = useState(false);
  const [secondaryCpId, setSecondaryCpId] = useState<number | null>(null);

  // Modal (create/edit)
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, per_page: 20 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (includeInactive) params.include_inactive = true;
      const res = await api.get('/counterparties', { params });
      setItems(res.data.items);
      setTotal(res.data.total);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole, includeInactive]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/counterparties/sync-from-1c');
      const s = res.data;
      notification.success({
        message: 'Справочник обновлён из 1С',
        description: `Новых: ${s.created}, связано: ${s.linked}, обновлено: ${s.updated} (всего в 1С: ${s.total})`,
      });
      await loadData();
    } catch (e: any) {
      notification.error({ message: 'Ошибка синка с 1С', description: e?.response?.data?.detail || 'Не удалось получить данные' });
    } finally {
      setSyncing(false);
    }
  };

  const loadContacts = useCallback(async (counterpartyId: number) => {
    setContactsLoading(true);
    try {
      const res = await api.get('/individuals', {
        params: { role: 'counterparty_employee', counterparty_id: counterpartyId, per_page: 100 },
      });
      setContacts(res.data.items || []);
    } catch {
      setContacts([]);
    } finally {
      setContactsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selected) {
      loadContacts(selected.id);
    } else {
      setContacts([]);
    }
  }, [selected, loadContacts]);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (cp: Counterparty) => {
    setEditingId(cp.id);
    form.setFieldsValue({
      short_name: cp.short_name,
      full_name: cp.full_name,
      inn: cp.inn,
      kpp: cp.kpp,
      ogrn: cp.ogrn,
      org_type: cp.org_type,
      legal_address: cp.legal_address,
      actual_address: cp.actual_address,
      phone: cp.phone,
      email: cp.email,
      website: cp.website,
      notes: cp.notes,
      roles: cp.roles.map(r => r.role),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        ...values,
        roles: (values.roles || []).map((r: string) => ({ role: r, contract_note: null })),
      };
      if (editingId) {
        const res = await api.put(`/counterparties/${editingId}`, payload);
        setItems(prev => prev.map(i => i.id === editingId ? res.data : i));
        if (selected?.id === editingId) {
          setSelected(res.data);
          setDrawerRefreshKey(prev => prev + 1);
        }
        message.success('Контрагент обновлён');
      } else {
        const res = await api.post('/counterparties', payload);
        setItems(prev => [res.data, ...prev]);
        message.success('Контрагент создан');
      }
      setModalOpen(false);
    } catch (e: any) {
      if (e?.response?.data?.detail) {
        message.error(e.response.data.detail);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (id: number) => {
    try {
      await api.delete(`/counterparties/${id}`);
      if (selected?.id === id) setSelected(prev => prev ? { ...prev, is_active: false } : null);
      loadData();
      message.success('Контрагент архивирован');
    } catch {
      message.error('Ошибка при архивировании');
    }
  };

  const columns = [
    {
      title: 'Наименование',
      dataIndex: 'short_name',
      key: 'name',
    },
    {
      title: 'ИНН',
      dataIndex: 'inn',
      key: 'inn',
      width: 130,
      render: (v: string) => v || <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Роли',
      key: 'roles',
      render: (_: any, r: Counterparty) =>
        r.roles.length > 0
          ? r.roles.map(role => (
              <Tag key={role.role} color={ROLE_COLORS[role.role]}>{role.label}</Tag>
            ))
          : <span style={{ color: '#ccc' }}>—</span>,
    },
    {
      title: 'Статус',
      key: 'status',
      width: 100,
      render: (_: any, r: Counterparty) =>
        r.is_active
          ? <Tag color="green">Активен</Tag>
          : <Tag color="default">Архив</Tag>,
    },
  ];

  return (
    <div>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Контрагенты</Title></Col>
        {canWrite && (
          <Col>
            <Space>
              <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync}>
                Обновить из 1С
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                Добавить
              </Button>
            </Space>
          </Col>
        )}
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={12} align="middle">
          <Col span={8}>
            <Input.Search
              placeholder="Поиск по названию, ИНН..."
              onSearch={v => { setSearch(v); setPage(1); }}
              onChange={e => { if (!e.target.value) { setSearch(''); setPage(1); } }}
              allowClear
            />
          </Col>
          <Col span={6}>
            <Select
              placeholder="Роль"
              allowClear
              style={{ width: '100%' }}
              onChange={(v) => { setFilterRole(v); setPage(1); }}
              options={ROLE_OPTIONS}
            />
          </Col>
          <Col>
            <Checkbox
              checked={includeInactive}
              onChange={e => { setIncludeInactive(e.target.checked); setPage(1); }}
            >
              Показать архивных
            </Checkbox>
          </Col>
        </Row>
      </Card>

      {/* Table */}
      <Table
        dataSource={items}
        columns={columns}
        rowKey="id"
        loading={loading}
        onRow={(record) => ({
          onClick: () => { setSelected(record); setDrawerOpen(true); },
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: p => setPage(p),
          showTotal: t => `Всего ${t}`,
        }}
      />

      {/* ── View Drawer ── */}
      <CounterpartyInfoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        counterpartyId={selected?.id ?? null}
        width={520}
        refreshKey={drawerRefreshKey}
        extra={
          canWrite && selected ? (
            <Button type="primary" onClick={() => openEdit(selected)}>
              Редактировать
            </Button>
          ) : null
        }
        onContactClick={openIndInfoDrawer}
      />

      {/* ── Create / Edit Modal ── */}
      <Modal
        title={editingId ? 'Редактировать контрагента' : 'Новый контрагент'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        width={680}
        destroyOnClose
        footer={
          <Row justify="space-between" align="middle">
            <Col>
              {editingId && selected?.is_active && (
                <Popconfirm
                  title="Архивировать контрагента?"
                  onConfirm={() => { handleArchive(editingId); setModalOpen(false); setDrawerOpen(false); }}
                  okText="Да"
                  cancelText="Нет"
                >
                  <Button danger>В архив</Button>
                </Popconfirm>
              )}
            </Col>
            <Col>
              <Space>
                <Button onClick={() => setModalOpen(false)}>Отмена</Button>
                <Button type="primary" loading={saving} onClick={handleSubmit}>
                  {editingId ? 'Сохранить' : 'Создать'}
                </Button>
              </Space>
            </Col>
          </Row>
        }
      >
        <Form form={form} layout="vertical">
          <Tabs
            items={[
              {
                key: 'details',
                label: 'Реквизиты',
                children: (
                  <>
                    <Row gutter={12}>
                      <Col span={14}>
                        <Form.Item
                          name="short_name"
                          label="Краткое наименование"
                          rules={[{ required: true, message: 'Обязательное поле' }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col span={10}>
                        <Form.Item name="org_type" label="Тип организации">
                          <Input placeholder="ООО, ИП, АО..." />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="full_name" label="Полное наименование">
                      <Input />
                    </Form.Item>
                    <Row gutter={12}>
                      <Col span={8}>
                        <Form.Item name="inn" label="ИНН"><Input /></Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="kpp" label="КПП"><Input /></Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="ogrn" label="ОГРН"><Input /></Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="legal_address" label="Юридический адрес">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Form.Item name="actual_address" label="Фактический адрес">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Row gutter={12}>
                      <Col span={8}>
                        <Form.Item name="phone" label="Телефон"><Input /></Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="email" label="Email"><Input /></Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="website" label="Сайт"><Input /></Form.Item>
                      </Col>
                    </Row>
                    <Form.Item name="notes" label="Заметки">
                      <Input.TextArea rows={3} />
                    </Form.Item>
                    <Form.Item name="roles" label="Роли">
                      <Checkbox.Group options={ROLE_OPTIONS} />
                    </Form.Item>
                  </>
                ),
              },
              ...(editingId ? [{
                key: 'contacts',
                label: 'Контакты',
                children: (
                  <div>
                    <Row justify="end" style={{ marginBottom: 8 }}>
                      {canWrite && (
                        <Button size="small" icon={<PlusOutlined />} onClick={() => setIndModalOpen(true)}>
                          Добавить
                        </Button>
                      )}
                    </Row>
                    {contactsLoading ? (
                      <div style={{ textAlign: 'center', padding: 16 }}><Spin size="small" /></div>
                    ) : contacts.length === 0 ? (
                      <div style={{ color: '#aaa', fontSize: 12 }}>Нет контактных лиц</div>
                    ) : (
                      contacts.map(c => {
                        const cName = [c.last_name, c.first_name, c.middle_name].filter(Boolean).join(' ');
                        return (
                          <Card
                            key={c.id}
                            size="small"
                            style={{ marginBottom: 8, cursor: 'pointer' }}
                            onClick={() => openIndInfoDrawer(c.id)}
                            extra={
                              canWrite ? (
                                <Button
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={e => { e.stopPropagation(); setEditIndividualId(c.id); setEditIndModalOpen(true); }}
                                />
                              ) : null
                            }
                          >
                            {cName && <div><strong>{cName}</strong></div>}
                            {c.job_title && <div style={{ color: '#555', fontSize: 13 }}>{c.job_title}</div>}
                            {c.phones?.map((p: string, i: number) => (
                              <div key={i} style={{ color: '#555', fontSize: 13 }}>{p}</div>
                            ))}
                            {c.emails?.map((e: string, i: number) => (
                              <div key={i} style={{ color: '#555', fontSize: 13 }}>
                                <a href={`mailto:${e}`} onClick={ev => ev.stopPropagation()}>{e}</a>
                              </div>
                            ))}
                            {c.notes && <div style={{ color: '#aaa', fontSize: 12 }}>{c.notes}</div>}
                          </Card>
                        );
                      })
                    )}
                  </div>
                ),
              }] : []),
            ]}
          />
        </Form>
      </Modal>

      <IndividualFormModal
        open={indModalOpen}
        onClose={() => setIndModalOpen(false)}
        defaultRole="counterparty_employee"
        defaultCounterpartyId={selected?.id}
        onCreated={() => { if (selected) loadContacts(selected.id); }}
      />

      <IndividualFormModal
        open={editIndModalOpen}
        onClose={() => { setEditIndModalOpen(false); setEditIndividualId(null); }}
        defaultRole="counterparty_employee"
        defaultCounterpartyId={selected?.id}
        editId={editIndividualId ?? undefined}
        onUpdated={() => { if (selected) loadContacts(selected.id); }}
      />

      <IndividualInfoDrawer
        open={indInfoOpen}
        onClose={() => setIndInfoOpen(false)}
        individualId={selectedIndId}
        onCounterpartyClick={(cpId) => { setSecondaryCpId(cpId); setSecondaryCpOpen(true); }}
      />

      <CounterpartyInfoDrawer
        open={secondaryCpOpen}
        onClose={() => setSecondaryCpOpen(false)}
        counterpartyId={secondaryCpId}
        onContactClick={(id) => { setSecondaryCpOpen(false); openIndInfoDrawer(id); }}
      />
    </div>
  );
};

export default CounterpartiesPage;
